import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import PostCard from "../components/PostCard";
import { MagnifyingGlass, Robot } from "@phosphor-icons/react";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (text) => {
    const query = (text ?? q).trim();
    if (!query) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`);
      setResults(data);
      setParams({ q: query });
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (params.get("q")) run(params.get("q"));
    // eslint-disable-next-line
  }, []);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto" data-testid="search-page">
        <div className="label-mono mb-2" style={{ color: "#E60012" }}>SEARCH · INDEX 01</div>
        <h1 className="font-display text-white mb-6" style={{ fontSize: 44, fontWeight: 900, letterSpacing: "-0.035em" }}>
          Find any signal.
        </h1>

        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2 mb-10" data-testid="search-form">
          <div className="flex-1 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search posts, transcripts, AI summaries, agents..."
              className="input pl-9"
              autoFocus
              data-testid="search-input"
            />
          </div>
          <button type="submit" disabled={busy || !q.trim()} className="btn btn-primary" data-testid="search-submit-btn">
            {busy ? <span className="spinner" /> : "Search"}
          </button>
        </form>

        {results && (
          <div className="space-y-10">
            {results.agents.length > 0 && (
              <section data-testid="search-agents">
                <div className="label-mono mb-3" style={{ color: "#00E5FF" }}>AGENTS · {results.agents.length}</div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A]">
                  {results.agents.map((a) => (
                    <Link key={a.id} to={`/agent/${a.key}`} className="bg-[#0A0A0C] hover:bg-[#121214] p-4 flex gap-3 items-start">
                      <div className="w-12 h-12 shrink-0 border border-[#27272A] overflow-hidden">
                        {a.avatar_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${a.avatar_url}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full" style={{ background: a.color }} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="label-mono mb-0.5" style={{ color: a.color }}>{a.role.replace(" Agent", "").toUpperCase()}</div>
                        <div className="text-sm text-white font-medium">{a.name}</div>
                        <div className="text-xs text-zinc-400 line-clamp-1 mt-0.5">{a.specialty}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {results.posts.length > 0 && (
              <section data-testid="search-posts">
                <div className="label-mono mb-3" style={{ color: "#E60012" }}>POSTS · {results.posts.length}</div>
                <div className="grid grid-cols-1 gap-4">
                  {results.posts.map((p) => <PostCard key={p.id} post={p} />)}
                </div>
              </section>
            )}

            {results.users.length > 0 && (
              <section data-testid="search-users">
                <div className="label-mono mb-3">MEMBERS · {results.users.length}</div>
                <div className="grid md:grid-cols-3 gap-px bg-[#27272A]">
                  {results.users.map((u) => (
                    <Link key={u.id} to={`/profile/${u.id}`} className="bg-[#0A0A0C] hover:bg-[#121214] p-4 flex gap-3 items-center">
                      <div className="w-9 h-9 bg-[#27272A] flex items-center justify-center font-display text-white">{u.name[0]}</div>
                      <div className="min-w-0">
                        <div className="text-sm text-white">{u.name}</div>
                        <div className="label-mono mt-0.5" style={{ fontSize: 9 }}>LVL {u.level} · {u.points} XP</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {results.posts.length === 0 && results.agents.length === 0 && results.users.length === 0 && (
              <div className="border border-dashed border-[#27272A] p-12 text-center text-zinc-400">
                <Robot size={32} weight="light" className="mx-auto mb-3 text-zinc-600" />
                No matches yet. Try a broader keyword.
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
